import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { IbanService, IbanValidity } from '@app/core/payment/iban.service';
import {
  SortCodeService,
  SortCodeValidity,
} from '@app/core/payment/sort-code.service';
import { Iban } from '@app/core/payment/types/iban';
import { ValidatorConfig } from '@fmnts/declarative-forms';
import { map, of, tap } from 'rxjs';
import { cbuValidator } from './validators/cbu.validator';

interface IbanValidatorArgs {
  available_countries: string[];
  disallowed_bank_codes?: string[];
  allowed_bank_codes?: string[];
}

const enum PaymnetValidatorName {
  /* eslint-disable-next-line @typescript-eslint/no-shadow */
  Iban = 'iban',
  // AR localized validations
  ArClaveBancariaUniforme = 'cbu',
  // UK localized validations
  UkSortCode = 'uk-sort-code',
  UkAccountNumber = 'uk-account-number',
}

@Injectable({
  providedIn: 'root',
})
export class PaymentValidationService {
  constructor(
    private ibanService: IbanService,
    private sortCodeService: SortCodeService,
  ) {}

  /**
   * @param validator The validator configuration
   *
   * @returns
   * An array of payment validator functions
   */
  public buildValidator(validator): ValidatorFn[] {
    switch (validator.name) {
      case PaymnetValidatorName.Iban:
        return [this.buildIbanValidator(validator)];
      case PaymnetValidatorName.ArClaveBancariaUniforme:
        return [cbuValidator()];
      default:
        return [];
    }
  }

  buildAsyncValidator(validator): AsyncValidatorFn[] {
    switch (validator.name) {
      case PaymnetValidatorName.UkSortCode:
        return [this.buildSortCodeValidator()];
      case PaymnetValidatorName.UkAccountNumber:
        return [this.buildSortCodeAccountNumberValidator()];
      default:
        return [];
    }
  }

  private buildIbanValidator(
    validator: ValidatorConfig<IbanValidatorArgs>,
  ): ValidatorFn {
    const { args } = validator;
    const { available_countries: countries } = args || {
      available_countries: undefined,
    };
    const { disallowed_bank_codes: disallowedBanks } = args || {
      disallowed_bank_codes: null,
    };
    const { allowed_bank_codes: allowedBanks } = args || {
      allowed_bank_codes: null,
    };

    return (control: AbstractControl) => {
      const value = (control.value || '').replace(' ', '');

      const bankId = this.ibanService.getBankInfo(new Iban(value))?.bankId;

      if (disallowedBanks && disallowedBanks.includes(bankId)) {
        return { unsupportedBankCode: true };
      }

      if (allowedBanks && !allowedBanks.includes(bankId)) {
        return { unsupportedBankCode: true };
      }
      const validity = this.ibanService.validate(value, countries);
      return IbanValidity.Valid === validity ? null : { iban: validity };
    };
  }

  /**
   * Only the sortcode is validated
   *
   * @returns
   * `null` when SortCode is valid, else an error-code
   */
  private buildSortCodeValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const value = (control.value || '').replace(' ', '');
      const accountNumberControl = control.parent?.get(
        'account_iban_or_number',
      );
      if (!accountNumberControl) {
        return of(null);
      }

      const accountNumber = accountNumberControl.value;

      return this.sortCodeService.validate(accountNumber, value).pipe(
        // Check validity for accountNumber control
        tap((validity) => {
          if (
            validity === SortCodeValidity.AccountNumberInvalid ||
            validity === SortCodeValidity.Invalid
          ) {
            accountNumberControl.setErrors({ bankAccountNumber: validity });
          } else if (
            SortCodeValidity.Valid === validity &&
            accountNumberControl.hasError('bankAccountNumber')
          ) {
            accountNumberControl.setErrors(
              this.filterErrorCode(accountNumberControl, 'bankAccountNumber'),
            );
            accountNumberControl.updateValueAndValidity();
          }
        }),
        // validity for sortCode control
        map((validity) =>
          SortCodeValidity.Valid === validity
            ? null
            : { bankAccountSortCode: validity },
        ),
      );
    };
  }

  /**
   * Validate the SortCode together with the AccountNumber
   *
   * @returns
   * `null` when SortCode combined with AccountNumber is valid,
   *  else an error-code
   */
  private buildSortCodeAccountNumberValidator(): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const value = (control.value || '').replace(' ', '');
      const sortCodeControl = control.parent?.get('account_bic_or_bankcode');
      if (!sortCodeControl) {
        return of(null);
      }
      const sortCode = sortCodeControl.value;

      return this.sortCodeService.validate(value, sortCode).pipe(
        // Check validity for sortcode control
        tap((validity) => {
          if (
            SortCodeValidity.SortCodeInvalid === validity ||
            SortCodeValidity.Invalid === validity
          ) {
            sortCodeControl.setErrors({ bankAccountSortCode: validity });
          } else if (
            SortCodeValidity.Valid === validity &&
            sortCodeControl.hasError('bankAccountSortCode')
          ) {
            sortCodeControl.setErrors(
              this.filterErrorCode(sortCodeControl, 'bankAccountSortCode'),
            );
            sortCodeControl.updateValueAndValidity();
          }
        }),
        // validity for accountNumber control
        map((validity) =>
          SortCodeValidity.Valid === validity
            ? null
            : { bankAccountNumber: validity },
        ),
      );
    };
  }

  private filterErrorCode(
    control: AbstractControl,
    errorCode: string,
  ): ValidationErrors {
    return {
      ...Object.entries(control.errors).reduce(
        (acc, [currentErrorCode, error]) =>
          currentErrorCode === errorCode
            ? acc
            : { ...acc, [currentErrorCode]: error },
      ),
    };
  }
}
